function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Section: Subscription Cards
// ----------------------------------------------------------------------------
//
// Subscription Cards Section
// - Displays configurable product cards with subscription options
// - Each option maps to a different product
// - Default mode: delivery buttons navigate to the option's PDP with
//   ?frequency= so the PDP preselects the matching selling plan
// - Direct Add to Cart mode (A/B test B version): a single button adds the
//   selected box type subscription straight to the cart (curated products go
//   through the Recharge dynamic bundle flow, mirroring the PDP), then sends
//   the shopper to the cart page. Any failure falls back to the PDP so the
//   shopper can always complete the purchase there.
// - Frequency lock (Build a Box cards, A/B test B version): when the card has
//   data-lock-frequency='true', every PDP navigation also carries
//   ?lock_frequency=1 so the recharge-bundle-frequency-lock snippet pins the
//   Recharge bundle widget to the requested frequency.
// - Auto-apply discount (A/B test B version): when the section has
//   data-auto-discount, the code is added to the cart on page view so the
//   cart and checkout already show the discounted price, and remembered for
//   the session so it is re-applied if something clears it (helpers/auto-discount).
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import { cart } from 'scripts/helpers/iamota-shopify-ajax';
import { buildCuratedBundleItems } from 'scripts/helpers/recharge-curated-bundle';
import { extractFrequencyFromText, findPlanIdByFrequency } from 'scripts/helpers/selling-plan-frequency';
import { ensureAutoDiscount, rememberAutoDiscount, watchAutoDiscount } from 'scripts/helpers/auto-discount';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/keyboard';

// Slider arrow + pagination styles
import 'snippets/swiper-controls/swiper-control';

// Styles
import './_subscription-cards.scss';

// -------------------------------------
// Variables
// -------------------------------------

var CARD_SELECTOR = '.subscription-card';
var OPTION_SELECTOR = '.subscription-card__option input[type="radio"]';
var BUTTON_SELECTOR = '[data-card-button]';
var ADD_TO_CART_SELECTOR = '[data-card-add-to-cart]';
var IMAGE_LINK_SELECTOR = '.subscription-card__image-link';
var DETAILS_LINK_SELECTOR = '[data-details-link]';
var SELECTED_CLASS = 'subscription-card__option--selected';
var LOADING_CLASS = 'subscription-card__button--loading';
var SLIDER_SELECTOR = '.subscription-cards--slider';
// Same boundary as the SCSS bp(tablet) breakpoint, so JS and CSS never disagree.
var GRID_QUERY = '(min-width: 768px)';
var ORDER_PROPERTIES = ['--order-mobile', '--order-desktop'];
var AUTO_DISCOUNT_SELECTOR = '[data-auto-discount]';
// Card buttons navigate away; give an in-flight auto-discount this long to land.
var AUTO_DISCOUNT_WAIT_MS = 2000;
var pendingAutoDiscount = Promise.resolve();
var waitForAutoDiscount = () => Promise.race([pendingAutoDiscount, new Promise(resolve => setTimeout(resolve, AUTO_DISCOUNT_WAIT_MS))]);
// Read by snippets/recharge-bundle-frequency-lock on the bundle product page.
var LOCK_FREQUENCY_PARAM = 'lock_frequency';

// -------------------------------------
// Helpers
// -------------------------------------

var isFrequencyLocked = card => {
  var _card$dataset;
  return (card === null || card === void 0 || (_card$dataset = card.dataset) === null || _card$dataset === void 0 ? void 0 : _card$dataset.lockFrequency) === 'true';
};

// The image link and "See more details" link are browsing links, not a
// frequency choice, so they always target Monthly when the card is locked —
// same as the Liquid-rendered initial hrefs (subscription-cards.liquid). This
// keeps switching box-type options from ever landing on the PDP unlocked.
export var buildBrowseUrl = (url, lockFrequency) => {
  if (!lockFrequency) return url;
  var separator = url.includes('?') ? '&' : '?';
  return "".concat(url).concat(separator, "frequency=monthly&").concat(LOCK_FREQUENCY_PARAM, "=1");
};

// The image link (opt-in via the section setting) points at the box type the
// shopper has selected, so it has to follow the radios.
var updateImageLink = (card, url) => {
  var imageLink = card.querySelector(IMAGE_LINK_SELECTOR);
  if (imageLink && url) {
    imageLink.href = buildBrowseUrl(url, isFrequencyLocked(card));
  }
};

// The See more details link (Add to Cart mode) also points at the box type
// the shopper has selected.
var updateDetailsLink = (card, url) => {
  var detailsLink = card.querySelector(DETAILS_LINK_SELECTOR);
  if (detailsLink && url) {
    detailsLink.href = buildBrowseUrl(url, isFrequencyLocked(card));
  }
};
var handleOptionChange = (card, option) => {
  var allOptions = card.querySelectorAll('.subscription-card__option');
  allOptions.forEach(opt => {
    opt.classList.remove(SELECTED_CLASS);
  });
  option.closest('.subscription-card__option').classList.add(SELECTED_CLASS);
  updateImageLink(card, option.value);
  updateDetailsLink(card, option.value);
};
var getSelectedOption = card => card.querySelector("".concat(OPTION_SELECTOR, ":checked"));

/**
 * Build the PDP URL a card button navigates to.
 * @param {string} url  Product URL (may already carry a query string)
 * @param {string} frequency  'monthly' | 'bimonthly'
 * @param {boolean} lockFrequency  Pin the frequency on the Build a Box page
 * @returns {string}
 */
export var buildPdpUrl = function buildPdpUrl(url, frequency) {
  var lockFrequency = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var separator = url.includes('?') ? '&' : '?';
  var lockParam = lockFrequency ? "&".concat(LOCK_FREQUENCY_PARAM, "=1") : '';
  return "".concat(url).concat(separator, "frequency=").concat(frequency).concat(lockParam);
};
var navigateToPdp = function navigateToPdp(url, frequency) {
  var lockFrequency = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (!url) return;
  window.location.href = buildPdpUrl(url, frequency, lockFrequency);
};
var handleContinue = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* (card, frequency) {
    var selectedOption = getSelectedOption(card);
    if (selectedOption) {
      yield waitForAutoDiscount();
      navigateToPdp(selectedOption.value, frequency, isFrequencyLocked(card));
    }
  });
  return function handleContinue(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
var parseBooleanFlag = value => {
  var normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
};
var parseJSON = function parseJSON(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  if (typeof value !== 'string' || !value.trim()) return defaultValue;
  try {
    return JSON.parse(value);
  } catch (error) {
    return defaultValue;
  }
};
var handleAddToCart = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (card, button) {
    var _selectedOption$close;
    var selectedOption = getSelectedOption(card);
    if (!selectedOption) return;
    var frequency = button.dataset.frequency || 'monthly';
    var optionData = ((_selectedOption$close = selectedOption.closest('.subscription-card__option')) === null || _selectedOption$close === void 0 ? void 0 : _selectedOption$close.dataset) || {};
    var pdpUrl = selectedOption.value;
    var lockFrequency = isFrequencyLocked(card);

    // The PDP is the safety net: any gap in the data or a failed cart request
    // falls back to the A behavior (navigate to the PDP), so the shopper can
    // always complete the purchase there.
    var variantId = optionData.variantId;
    var requestedFrequency = extractFrequencyFromText(frequency);
    var sellingPlanId = findPlanIdByFrequency(parseJSON(optionData.sellingPlans, []), requestedFrequency);
    if (!variantId || !sellingPlanId) {
      navigateToPdp(pdpUrl, frequency, lockFrequency);
      return;
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add(LOADING_CLASS);
    try {
      var _window$Shopify;
      if (parseBooleanFlag(optionData.isCurated)) {
        var items = yield buildCuratedBundleItems({
          parentProductId: optionData.productId,
          parentVariantId: variantId,
          sellingPlanId,
          collectionId: optionData.bundleCollectionId,
          presetProductId: optionData.presetProductId,
          presetVariantId: optionData.presetVariantId,
          productHandle: optionData.productHandle
        });
        yield cart.addItems(items);
      } else {
        yield cart.addItem({
          id: Number(variantId),
          quantity: 1,
          selling_plan: Number(sellingPlanId)
        });
      }
      yield waitForAutoDiscount();
      window.location.href = "".concat(((_window$Shopify = window.Shopify) === null || _window$Shopify === void 0 || (_window$Shopify = _window$Shopify.routes) === null || _window$Shopify === void 0 ? void 0 : _window$Shopify.root) || '/', "cart");
    } catch (error) {
      console.error('Subscription card add to cart failed, falling back to the product page.', error);
      yield waitForAutoDiscount();
      navigateToPdp(pdpUrl, frequency, lockFrequency);
    }
  });
  return function handleAddToCart(_x3, _x4) {
    return _ref2.apply(this, arguments);
  };
}();
var initSubscriptionCards = function initSubscriptionCards() {
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  var cards = root.querySelectorAll(CARD_SELECTOR);
  cards.forEach(card => {
    // Avoid binding the same card twice (initial load + section reload).
    if (card.dataset.scBound === 'true') return;
    card.dataset.scBound = 'true';
    var options = card.querySelectorAll(OPTION_SELECTOR);
    var buttons = card.querySelectorAll(BUTTON_SELECTOR);
    var addToCartButtons = card.querySelectorAll(ADD_TO_CART_SELECTOR);
    options.forEach(option => {
      option.addEventListener('change', () => {
        handleOptionChange(card, option);
      });
    });
    buttons.forEach(button => {
      button.addEventListener('click', () => {
        var frequency = button.dataset.frequency;
        handleContinue(card, frequency);
      });
    });
    addToCartButtons.forEach(button => {
      button.addEventListener('click', () => {
        handleAddToCart(card, button);
      });
    });
  });
};
var createSlider = (slider, swiperEl) => {
  var isFullWidthSlider = slider.dataset.sliderFullWidth === 'true';
  return new Swiper(swiperEl, {
    // The default slider mirrors the grid density. Full-width mode is
    // a separate merchant option for comparing one card per view.
    slidesPerView: 1,
    spaceBetween: 16,
    breakpoints: isFullWidthSlider ? {} : {
      768: {
        slidesPerView: 2,
        spaceBetween: 16
      },
      1080: {
        slidesPerView: 3,
        spaceBetween: 24
      }
    },
    modules: [Navigation, Pagination, Keyboard],
    loop: false,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    keyboard: {
      enabled: true
    },
    navigation: {
      nextEl: slider.querySelector('.swiper-button-next'),
      prevEl: slider.querySelector('.swiper-button-prev')
    },
    pagination: {
      el: slider.querySelector('.swiper-pagination'),
      type: 'bullets',
      clickable: true
    }
  });
};

// swiper.destroy(true, true) strips each slide's inline style, which also
// holds the card order vars the grid relies on, so keep them across it.
var destroySlider = swiper => {
  var slides = [...swiper.slides];
  var orders = slides.map(slide => ORDER_PROPERTIES.map(prop => slide.style.getPropertyValue(prop)));
  swiper.destroy(true, true);
  slides.forEach((slide, index) => {
    ORDER_PROPERTIES.forEach((prop, propIndex) => {
      if (orders[index][propIndex]) slide.style.setProperty(prop, orders[index][propIndex]);
    });
  });
};

// "Only use the slider on mobile": Swiper only lives below the tablet
// breakpoint. From tablet up it is destroyed and the SCSS lays the same
// markup out as the standard grid.
var initMobileOnlySlider = (slider, swiperEl) => {
  var gridQuery = window.matchMedia(GRID_QUERY);
  var swiper = null;
  var update = () => {
    if (!gridQuery.matches && !swiper) {
      swiper = createSlider(slider, swiperEl);
    } else if (gridQuery.matches && swiper) {
      destroySlider(swiper);
      swiper = null;
    }
  };
  update();
  // addListener keeps older Safari working (no addEventListener on MediaQueryList).
  gridQuery.addListener(update);
};
var initSubscriptionSliders = function initSubscriptionSliders() {
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  var sliders = root.querySelectorAll(SLIDER_SELECTOR);
  sliders.forEach(slider => {
    var cardCount = parseInt(slider.dataset.cardCount || '0', 10);

    // Arrows/pagination aren't rendered for a single card, so skip init.
    if (cardCount <= 1) return;
    var swiperEl = slider.querySelector('.subscription-cards__swiper');
    // Skip if missing or this slider was already set up (section reload).
    if (!swiperEl || slider.dataset.sliderBound === 'true') {
      return;
    }
    slider.dataset.sliderBound = 'true';
    if (slider.dataset.sliderMobileOnly === 'true') {
      initMobileOnlySlider(slider, swiperEl);
      return;
    }
    createSlider(slider, swiperEl);
  });
};
var applyAutoDiscount = function applyAutoDiscount() {
  var _window$Shopify2;
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  var codes = [...root.querySelectorAll(AUTO_DISCOUNT_SELECTOR)].map(el => el.dataset.autoDiscount).filter(code => code && code.trim());
  if (!codes.length || (_window$Shopify2 = window.Shopify) !== null && _window$Shopify2 !== void 0 && _window$Shopify2.designMode) return Promise.resolve();

  // Remember the code for the session so other pages keep it on the cart
  // (see helpers/auto-discount for why one apply is not enough).
  rememberAutoDiscount(codes);
  watchAutoDiscount();
  return ensureAutoDiscount(codes);
};
var init = function init() {
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  initSubscriptionCards(root);
  initSubscriptionSliders(root);
  pendingAutoDiscount = applyAutoDiscount(root);
};

// -------------------------------------
// Hooks
// -------------------------------------

// Run on initial load (or immediately if the DOM is already parsed).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Shopify theme editor: re-initialise when a section is (re)rendered, e.g.
// when an admin toggles the slider on. DOMContentLoaded does not fire again,
// so without this the freshly rendered slider would never start.
document.addEventListener('shopify:section:load', event => {
  init(event.target);
});